import { useMutation, useQueryClient } from 'react-query';

import {
  DeltaIntervalType,
  ProceduralJurisdiction,
} from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import type { ProceduralTimelineEvent } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { parseErrorMessage } from '@/api/parse-error';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface DeltaIntervalInput {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export interface CreateProceduralEventInput {
  name: string;
  description?: string;
  timeZone?: string;
  startDateTime?: string;
  endDateTime?: string;
  anchorEventId?: string;
  deltaInterval?: DeltaIntervalInput;
  deltaIntervalType?: DeltaIntervalType;
  jurisdiction?: ProceduralJurisdiction;
  isConfirmed?: boolean;
}

interface CreateProceduralEventParams {
  matterId: string;
  input: CreateProceduralEventInput;
}

export const useCreateProceduralEvent = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<ProceduralTimelineEvent, Error, CreateProceduralEventParams>(
    async ({ matterId, input }) => {
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/events`,
        getFetchConfig({ method: FetchMethod.POST, data: input }),
      );
      if (!res.ok) {
        throw new Error((await parseErrorMessage(res)) || 'Failed to create event');
      }
      return res.json();
    },
    {
      onSuccess: (_, { matterId, input }) => {
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineEvents, { matterId }]);
        trackCustomEvent(TrackingEventsV2.ProceduralTimelineEventCreate, {
          matterId,
          hasAnchor: input.anchorEventId !== undefined,
          hasInterval: input.deltaInterval !== undefined,
        });
      },
    },
  );
};
